import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faNewspaper,
  faCheck,
  faStar,
  faCalendar,
  faTag,
  faImage,
  faUpload,
  faSpinner,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabase";
import { useImageUpload } from "../../hooks/useContent";

const ArticlesManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image_url: "",
    author: "",
    category: "medical",
    tags: [],
    status: "draft",
    is_featured: false,
    reading_time_minutes: 5,
  });
  const [message, setMessage] = useState("");
  
  // Image upload functionality
  const { uploadImage, uploading, error: uploadError } = useImageUpload();
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;

      setArticles(data || []);
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "tags") {
      // Convert comma-separated string to array
      const tagsArray = value.split(",").map(tag => tag.trim()).filter(tag => tag);
      setFormData((prev) => ({ ...prev, [name]: tagsArray }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special chars
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/--+/g, "-") // Replace multiple - with single -
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData((prev) => ({ 
      ...prev, 
      title,
      // Only auto-generate slug if we're creating a new article or if slug is empty
      slug: !editingArticle || !prev.slug ? generateSlug(title) : prev.slug
    }));
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setTimeout(() => {
      setMessage("");
    }, 3000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage("Vă rugăm să selectați un fișier imagine valid (jpg, png, webp)", "error");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showMessage("Imaginea este prea mare. Vă rugăm să selectați o imagine mai mică de 5MB", "error");
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return;
    
    try {
      const imageUrl = await uploadImage(selectedFile);
      setFormData({
        ...formData,
        featured_image_url: imageUrl
      });
      setSelectedFile(null);
      showMessage("Imagine încărcată cu succes!", "success");
    } catch (error) {
      console.error('Upload error:', error);
      showMessage("Eroare la încărcarea imaginii", "error");
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featured_image_url: "",
      author: "",
      category: "medical",
      tags: [],
      status: "draft",
      is_featured: false,
      reading_time_minutes: 5,
    });
    setEditingArticle(null);
    setShowForm(false);
    setSelectedFile(null);
  };

  const handleAddArticle = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare the data
      const articleData = {
        ...formData,
        published_at: formData.status === "published" ? new Date().toISOString() : null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      // Insert the article
      const { data, error } = await supabase
        .from("articles")
        .insert([articleData])
        .select();

      if (error) throw error;

      setArticles((prev) => [...prev, data[0]]);
      showMessage("Articol adăugat cu succes!");
      resetForm();
      
      // Refresh the list
      fetchArticles();
    } catch (error) {
      console.error("Error adding article:", error);
      showMessage(`Eroare la adăugarea articolului: ${error.message}`, "error");
    }
  };

  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare the data
      const articleData = {
        ...formData,
        published_at: formData.status === "published" && !editingArticle.published_at 
          ? new Date().toISOString() 
          : editingArticle.published_at,
        updated_at: new Date().toISOString(),
      };
      
      // Update the article
      const { data, error } = await supabase
        .from("articles")
        .update(articleData)
        .eq("id", editingArticle.id)
        .select();

      if (error) throw error;

      setArticles((prev) => 
        prev.map((article) => (article.id === editingArticle.id ? data[0] : article))
      );
      showMessage("Articol actualizat cu succes!");
      resetForm();
      
      // Refresh the list
      fetchArticles();
    } catch (error) {
      console.error("Error updating article:", error);
      showMessage(`Eroare la actualizarea articolului: ${error.message}`, "error");
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Ești sigur că vrei să ștergi acest articol?")) return;
    
    try {
      const { error } = await supabase
        .from("articles")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setArticles((prev) => prev.filter((article) => article.id !== id));
      showMessage("Articol șters cu succes!");
    } catch (error) {
      console.error("Error deleting article:", error);
      showMessage(`Eroare la ștergerea articolului: ${error.message}`, "error");
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt || "",
      content: article.content,
      featured_image_url: article.featured_image_url || "",
      author: article.author || "",
      category: article.category || "medical",
      tags: article.tags || [],
      status: article.status || "draft",
      is_featured: article.is_featured || false,
      reading_time_minutes: article.reading_time_minutes || 5,
    });
    setShowForm(true);
    // Clear image upload state
    setSelectedFile(null);
  };

  const handleToggleStatus = async (article) => {
    try {
      const newStatus = article.status === "published" ? "draft" : "published";
      const published_at = newStatus === "published" && !article.published_at 
        ? new Date().toISOString() 
        : article.published_at;
        
      const { data, error } = await supabase
        .from("articles")
        .update({ 
          status: newStatus,
          published_at,
          updated_at: new Date().toISOString()
        })
        .eq("id", article.id)
        .select();

      if (error) throw error;

      setArticles((prev) => 
        prev.map((a) => (a.id === article.id ? data[0] : a))
      );
      showMessage(`Articol ${newStatus === "published" ? "publicat" : "trecut în ciornă"}!`);
    } catch (error) {
      console.error("Error toggling article status:", error);
      showMessage(`Eroare la schimbarea statusului: ${error.message}`, "error");
    }
  };

  const handleToggleFeatured = async (article) => {
    try {
      const { data, error } = await supabase
        .from("articles")
        .update({ 
          is_featured: !article.is_featured,
          updated_at: new Date().toISOString()
        })
        .eq("id", article.id)
        .select();

      if (error) throw error;

      setArticles((prev) => 
        prev.map((a) => (a.id === article.id ? data[0] : a))
      );
      showMessage(`Articol ${!article.is_featured ? "marcat ca recomandat" : "demarcat ca recomandat"}!`);
    } catch (error) {
      console.error("Error toggling featured status:", error);
      showMessage(`Eroare la schimbarea statusului recomandat: ${error.message}`, "error");
    }
  };

  // Format tags for display
  const formatTags = (tags) => {
    if (!tags || !Array.isArray(tags) || tags.length === 0) return "Fără etichete";
    return tags.join(", ");
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "published":
        return "badge bg-success";
      case "draft":
        return "badge bg-warning text-dark";
      case "archived":
        return "badge bg-secondary";
      default:
        return "badge bg-info";
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case "published":
        return "Publicat";
      case "draft":
        return "Ciornă";
      case "archived":
        return "Arhivat";
      default:
        return status;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("ro-RO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="articles-manager">
      <div className="manager-header">
        <div className="header-content">
          <h1>
            <FontAwesomeIcon icon={faNewspaper} className="me-2" />
            Manager Articole
          </h1>
          <p>Adaugă, editează și gestionează articolele de pe site</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Adaugă Articol Nou
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.includes("Eroare") ? "alert-danger" : "alert-success"}`}>
          {message}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {showForm && (
        <div className="article-form-container">
          <form onSubmit={editingArticle ? handleUpdateArticle : handleAddArticle}>
            <div className="form-header">
              <h3>
                {editingArticle ? "Editează Articol" : "Adaugă Articol Nou"}
              </h3>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="row">
              <div className="col-md-8">
                <div className="mb-3">
                  <label className="form-label">Titlu articol</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Titlul articolului"
                    required
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Slug (URL)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    placeholder="slug-url-articol"
                    required
                  />
                  <small className="form-text text-muted">
                    URL-ul articolului (fără spații, doar litere mici, cifre și cratime)
                  </small>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Descriere scurtă (excerpt)</label>
              <textarea
                className="form-control"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="2"
                placeholder="O scurtă descriere a articolului (150-200 caractere)"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Conținut articol (HTML)</label>
              <textarea
                className="form-control"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows="10"
                placeholder="Conținutul articolului în format HTML"
                required
              />
              <small className="form-text text-muted">
                Puteți folosi HTML pentru formatare (h2, p, ul, li, strong, em, etc.)
              </small>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Imagine principală articol</label>
                  
                  {/* Show current image if exists */}
                  {formData.featured_image_url && (
                    <div className="mb-2">
                      <img 
                        src={formData.featured_image_url} 
                        alt="Preview" 
                        className="img-thumbnail"
                        style={{ width: '200px', height: '120px', objectFit: 'cover' }}
                      />
                      <div className="mt-1">
                        <small className="text-success">
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          Imagine încărcată cu succes
                        </small>
                      </div>
                    </div>
                  )}

                  {/* File input */}
                  <div className="input-group">
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={uploading}
                    />
                    {selectedFile && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleImageUpload}
                        disabled={uploading}
                      >
                        {uploading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                            Se încarcă...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUpload} className="me-1" />
                            Încarcă
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Selected file info */}
                  {selectedFile && (
                    <div className="mt-2">
                      <small className="text-info">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Fișier selectat: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </small>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-2"
                        onClick={clearSelectedFile}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}

                  {/* Upload error */}
                  {uploadError && (
                    <div className="mt-2">
                      <small className="text-danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        Eroare la încărcare: {uploadError.message}
                      </small>
                    </div>
                  )}

                  {/* Manual URL input as fallback */}
                  <div className="mt-2">
                    <details>
                      <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Sau introduceți manual URL-ul imaginii
                      </summary>
                      <div className="mt-2">
                        <input
                          type="text"
                          className="form-control"
                          name="featured_image_url"
                          value={formData.featured_image_url}
                          onChange={handleInputChange}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </details>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Autor</label>
                  <input
                    type="text"
                    className="form-control"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Numele autorului"
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Categorie</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="medical">Medicină generală</option>
                    <option value="pneumologie">Pneumologie</option>
                    <option value="apnee">Apnee de somn</option>
                    <option value="nutritie">Nutriție</option>
                    <option value="preventie">Prevenție</option>
                    <option value="stiri">Știri medicale</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="draft">Ciornă</option>
                    <option value="published">Publicat</option>
                    <option value="archived">Arhivat</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Timp de citire (minute)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="reading_time_minutes"
                    value={formData.reading_time_minutes}
                    onChange={handleInputChange}
                    min="1"
                    max="60"
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-8">
                <div className="mb-3">
                  <label className="form-label">Etichete (tags)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="tags"
                    value={Array.isArray(formData.tags) ? formData.tags.join(", ") : ""}
                    onChange={handleInputChange}
                    placeholder="tag1, tag2, tag3"
                  />
                  <small className="form-text text-muted">
                    Separate prin virgulă (ex: pneumologie, tuberculoza, tratament)
                  </small>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3 mt-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_featured"
                      id="is_featured"
                      checked={formData.is_featured}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="is_featured">
                      Articol recomandat
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {editingArticle ? "Actualizează" : "Adaugă"} Articol
              </button>
              <button
                type="button"
                className="btn btn-secondary ms-2"
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Anulează
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="articles-list mt-4">
        <h3>Articole ({articles.length})</h3>

        {loading ? (
          <div className="loading">Se încarcă articolele...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : articles.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faNewspaper} size="3x" className="text-muted mb-3" />
            <h3>Niciun articol găsit</h3>
            <p>Adaugă primul articol pentru blog.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> Adaugă Primul Articol
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Titlu</th>
                  <th>Categorie</th>
                  <th>Status</th>
                  <th>Autor</th>
                  <th>Publicat</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id}>
                    <td>
                      <div className="article-title-cell">
                        {article.is_featured && (
                          <span className="featured-badge me-2" title="Articol recomandat">
                            <FontAwesomeIcon icon={faStar} className="text-warning" />
                          </span>
                        )}
                        {article.title}
                        <div className="article-meta">
                          <small className="text-muted">
                            <FontAwesomeIcon icon={faTag} className="me-1" />
                            {formatTags(article.tags)}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{article.category}</td>
                    <td>
                      <span className={getStatusBadgeClass(article.status)}>
                        {getStatusText(article.status)}
                      </span>
                    </td>
                    <td>{article.author || "Necunoscut"}</td>
                    <td>
                      {article.published_at ? (
                        <span title={formatDate(article.published_at)}>
                          <FontAwesomeIcon icon={faCalendar} className="me-1" />
                          {formatDate(article.published_at)}
                        </span>
                      ) : (
                        <span className="text-muted">Nepublicat</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleEditArticle(article)}
                          title="Editează"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success me-1"
                          onClick={() => handleToggleStatus(article)}
                          title={article.status === "published" ? "Marchează ca ciornă" : "Publică"}
                        >
                          <FontAwesomeIcon icon={article.status === "published" ? faTimes : faCheck} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-warning me-1"
                          onClick={() => handleToggleFeatured(article)}
                          title={article.is_featured ? "Demarchează ca recomandat" : "Marchează ca recomandat"}
                        >
                          <FontAwesomeIcon icon={faStar} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteArticle(article.id)}
                          title="Șterge"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesManager; 